import { defineStore } from 'pinia'
import  { jwtDecode } from 'jwt-decode'
import type { Credentials } from '~/types/auth'
import type { LoginOptions } from '~/composables/auth'
import { stringifyQuery } from '~/utils'
import { assertIsDefined } from '~/utils/assert'
import { getAuthKeyFromSetCookies, getIsAgentImpersonatedFromCookie, getIsAgentLoggedInFromCookie, getIsDemoFromCookie } from '~/utils/auth'
import { useRootStore } from '~/stores/root'
import { usePersistentStore } from '~/stores/persistent'
import { UserRole } from '~/types/account'

export const useAuthStore = defineStore('auth', () => {
  const nuxtApp = useNuxtApp()
  const { user, login, userRole } = useSAuth()
  const persistentStore = usePersistentStore()
  const authMenu = ref(false)

  const isAgent = computed<boolean>(() => {
    return userRole.value === UserRole.Agent
  })

  const isEmployee = computed<boolean>(() =>
    user.value.email?.endsWith('@stampinup.com') && !isNonEmailCustomer.value
  )

  const isNonEmailCustomer = computed<boolean>(() => {
    return /no-email\+\d{1,10}@stampinup.com/i.test(user.value.email)
  })

  const isSilverEliteOrAbove = computed<boolean>(() => {
    return (user.value.demonstrator?.title ?? 0) > 3
  })

  const updateAuthMenu = (show: boolean) => {
    authMenu.value = show
    if (!show) {
      const rootStore = useRootStore()
      rootStore.redirectRoute = ''
      rootStore.authMessage = ''
    }
    return authMenu.value
  }

  const authenticate = async (credentials: Credentials, options?: LoginOptions) => {
    return await login({
        username: credentials.username,
        password: credentials.password,
        persist: credentials.persist
    }, options)
  }

  const loginViaToken = async (loginViaTokenRequest: { authToken?: string, sharedCartId?: string }) => {
    if (import.meta.server && loginViaTokenRequest.authToken) {
      const response = await $api().raw<unknown>(`account/sign-in${stringifyQuery(loginViaTokenRequest)}`, { method: 'POST', body: {} })
      // decode the jwt token to get the user
      // if the actor is set then we should be an agent impersonating a customer or demonstrator
      // for logging purposes only for instances of someone impersonating and not being an agent
      const jwtUser = jwtDecode<{ actor: string, email: string }>(loginViaTokenRequest.authToken)
      let jwtActor
      if (jwtUser.actor) {
        jwtActor = jwtDecode<{ email: string, role: string }>(jwtUser.actor)
        if (jwtActor.role !== 'Agent') {
          nuxtApp.$appInsights.trackTrace({
            message: `IMPERSONATING TRACE - actorEmail: ${jwtActor.email}, userEmail: ${jwtUser.email}`
          })
        }
      }

      assertIsDefined(response.headers.getSetCookie(), 'set cookie header should be set when login via token')
      const cookieHeader = response.headers.getSetCookie().join(';')
      // Only auth_cookie can appear twice in this reply (see getAuthKeyFromSetCookies); the flags below appear once
      const token = getAuthKeyFromSetCookies(response.headers.getSetCookie())
      const isDemo = getIsDemoFromCookie(cookieHeader)
      const isAgentLoggedIn = getIsAgentLoggedInFromCookie(cookieHeader)
      const isAgentImpersonated = getIsAgentImpersonatedFromCookie(cookieHeader)
      // additional logging for someone impersonating and not being an agent
      if (!isAgentLoggedIn && isAgentImpersonated) {
        nuxtApp.$appInsights.trackTrace({
          message: `IMPERSONATING TRACE - userEmail: ${jwtUser.email} authEmail: ${user.value.email}`
        })
      }
      assertIsDefined(token, 'token cookie header should be set when login via token')
      const auth = useSAuth()
      auth.setUserToken(token)
      auth.setAuthCookie(token)
      await auth.fetchSession()

      // Through the persistent store, so isDemo is written in the one scope every
      // other isDemo write uses.
      persistentStore.updateValue('isDemo', !!isDemo)
      const isAgentLoggedInCookie = useCookie<boolean>('isAgentLoggedIn', { secure: true, httpOnly: false })
      isAgentLoggedInCookie.value = !!isAgentLoggedIn
      if (isAgentImpersonated) {
        const isAgentImpersonatedCookie = useCookie<boolean>('isAgentImpersonated', { secure: true, httpOnly: false })
        isAgentImpersonatedCookie.value = !!isAgentImpersonated
      }
      if (jwtActor && jwtActor.email) {
        useCookie('agentEmail', { secure: true, httpOnly: false }).value = jwtActor.email
      }
    } else {
      throw new Error('login via token called from client. Expected to be called only from server')
    }
  }

  return {
    authMenu,
    isAgent,
    isEmployee,
    isNonEmailCustomer,
    isSilverEliteOrAbove,
    updateAuthMenu,
    authenticate,
    loginViaToken
  }
})
